import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  HomeAdminZone,
  type HomeAdminZoneProps,
} from "@/components/v2/home/HomeAdminZone";

/**
 * "Matchweek ticket" shell — shared by all 5 Home phases. Departs from a
 * plain bordered Card: a header panel up top carries the question +
 * a circular hero visual (ring or icon), then the primary action floats as
 * a stub card overlapping the panel boundary so it's unmissable on every
 * phase, then secondary detail recedes below a dashed fold.
 */
interface HomePhaseShellProps {
  eyebrow: string;
  title: string;
  eyebrowBadge?: ReactNode;
  /** Date / location / weather — small, muted, sits under the title. */
  meta?: ReactNode;
  /** Short emphasized line under meta (countdown, quorum flash, etc). */
  heroNote?: ReactNode;
  /** Top-right action (e.g. share link) — rendered inside the card header. */
  headerAction?: ReactNode;
  /** Circular ring or icon-in-circle — the one glanceable number/status. */
  hero?: ReactNode;
  /** Main phase-specific content (vote grid, roster chips, scoreboard...). */
  children?: ReactNode;
  /** The unmissable action — rendered as a floating stub card. */
  primaryAction?: ReactNode;
  /** De-emphasized content — roster, history, teaser. */
  secondary?: ReactNode;
  /** Admin-only controls — always rendered below member-facing content. */
  adminSection?: ReactNode;
  /** Wraps {@link adminSection} in the amber organizer panel. */
  adminZone?: Pick<HomeAdminZoneProps, "title" | "description" | "actionRequired">;
  /** Small trailing actions (cancel match, dispute, rebalance...). */
  footer?: ReactNode;
  accent?: "default" | "success" | "danger";
  className?: string;
}

export function HomePhaseShell({
  eyebrow,
  title,
  eyebrowBadge,
  meta,
  heroNote,
  headerAction,
  hero,
  children,
  primaryAction,
  secondary,
  adminSection,
  adminZone,
  footer,
  accent = "default",
  className,
}: HomePhaseShellProps) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[28px] border bg-card shadow-sm",
        accent === "success" ? "border-soccer-primary/40" : "border-border",
        accent === "danger" && "border-destructive/40",
        className
      )}
    >
      <div className="px-4 pb-7 pt-5 sm:px-7 sm:pt-7">
        <div className="flex items-start justify-between gap-3 sm:gap-4">
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-soccer-primary-700 dark:text-soccer-primary">
                {eyebrow}
              </span>
              {eyebrowBadge}
            </div>
            <h2 className="text-xl font-black leading-tight tracking-tight sm:text-[1.75rem]">
              {title}
            </h2>
            {meta && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-0.5 text-[13px] text-muted-foreground sm:gap-x-4 sm:text-sm">
                {meta}
              </div>
            )}
            {heroNote && <div className="pt-1 text-sm font-semibold">{heroNote}</div>}
          </div>
          {(headerAction || hero) && (
            <div className="flex shrink-0 items-start gap-2">
              {headerAction}
              {hero}
            </div>
          )}
        </div>
      </div>

      {primaryAction && (
        <div className="relative z-10 -mt-5 px-4 sm:px-7">{primaryAction}</div>
      )}

      <div
        className={cn(
          "space-y-4 px-4 pb-5 sm:px-7 sm:pb-6",
          primaryAction ? "pt-5" : "pt-1"
        )}
      >
        {children}
        {secondary && <div className="border-t border-dashed pt-4">{secondary}</div>}
        {adminSection && (
          <div className="border-t border-dashed border-border/80 pt-4">
            {adminZone ? (
              <HomeAdminZone {...adminZone}>{adminSection}</HomeAdminZone>
            ) : (
              adminSection
            )}
          </div>
        )}
        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-dashed pt-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function HomePhaseBadge({
  children,
  tone = "amber",
}: {
  children: ReactNode;
  tone?: "amber" | "primary";
}) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "font-semibold",
        tone === "amber" &&
          "border-amber-400/40 bg-amber-50 text-amber-700 dark:bg-amber-400/10 dark:text-amber-300",
        tone === "primary" &&
          "border-soccer-primary/40 text-soccer-primary-800 bg-soccer-primary/10 dark:text-soccer-primary"
      )}
    >
      {children}
    </Badge>
  );
}
